import type { EmailTemplatesQueryParamsRequest } from 'pages/settings/email-templates/types.ts';
import type { CompositeEntityId } from '../services/company-and-contacts/mappers.ts';
import type { MigrationCompanyId, MigrationPersonId } from '../services/entities/MigrationEntities.ts';

export const Endpoints = {
  getCurrentUser: '/user/me',
  getLegacyUser: '/user',
  getCurrentAccount: '/account',
  getAccountPlan: (accountId: number) => `/accounts/${accountId}/plan`,
  applyPromocode: (accountId: number) => `/accounts/${accountId}/promos`,
  getBillingInfo: (accountId: number) => `/accounts/${accountId}/billing`,
  updateBillingInfo: (accountId: number) => `/accounts/${accountId}/billing`,
  getBillingHistory: (accountId: number) => `/accounts/${accountId}/billing-history`,
  getBillingOptions: (accountId: number) => `/accounts/${accountId}/billing-options`,
  getSubscription: (accountId: number) => `/accounts/${accountId}/subscription`,
  updatePlan: (accountId: number) => `/accounts/${accountId}/subscription`,
  validateCoupon: (accountId: number) => `/accounts/${accountId}/promos`,
  getUserIntegrations: (userId: number | null) => `/user/${userId}/integrations`,
  getIntegrationOauthData: (userId: number, integrationName: string) => `/user/${userId}/integrations/${integrationName}/connectors`,
  getUserPermissions: '/user/me/role',
  getProposalPreviewUrl: (proposalId: number) => `/preview_url/${proposalId}`,
  getProposalShareUrl: (proposalId: number) => `/proposal/${proposalId}/share_email`,
  getProposalById: (proposalId: number) => `/proposal/${proposalId}`,
  getUserFeatureFlagContext: (userId: string | number, featureFlag: string) => `user/${userId}/flag/${featureFlag}`,
  // TODO(SP-2757): assumed contract for anonymous flag evaluation, not yet implemented by the
  // core API (api.proposify.com) — confirm the real path with the core-API team before relying
  // on this returning anything but a 404.
  getAnonymousFeatureFlagContext: (sessionId: string, featureFlag: string) => `flag/${featureFlag}/anonymous/${sessionId}`,
  getPersonById: (personId: MigrationPersonId) => `/person/${personId}`,
  getPersons: '/person',
  getCompanyById: (companyId: MigrationCompanyId) => `/company/${companyId}`,
  getCompaniesById: (companyId: MigrationCompanyId) => `/companies/${companyId}/contacts`,
  getCompanies: '/companies',
  getProspects: '/prospects',
  getShareProposalDefaultTemplate: (proposalId: number) => `/proposal/${proposalId}/share_template`,
  getShareProposalTemplate: ({ proposalId, templateId }: { proposalId: number; templateId: string }) =>
    `/proposal/${proposalId}/share_template/${templateId}`,
  getAllLegacyEmailTemplates: '/email_template',
  createEmailTemplate: '/email-template',
  deleteEmailTemplate: (eventType: string, versionSegment: string) => `/email-template/${eventType}/${versionSegment}`,
  getEmailTemplate: (scope: string, eventType: string, versionSegment: string) => `/email-template/${scope}/${eventType}/${versionSegment}`,
  listEmailTemplates: (params?: EmailTemplatesQueryParamsRequest) => {
    const search = new URLSearchParams();

    if (params?.scope) search.set('scope', params.scope);
    if (params?.eventType) search.set('eventType', params.eventType);
    if (params?.versionSegment) search.set('versionSegment', params.versionSegment);
    if (params?.limit && !isNaN(params.limit)) search.set('limit', String(params.limit));
    if (params?.nextToken) search.set('nextToken', params.nextToken);

    return `/email-template?${search.toString()}`;
  },
  updateEmailTemplate: (existingVersionSegment?: string) => `/email-template/${existingVersionSegment ?? ''}`,
  markProposalAsSent: (proposalId: number) => `/proposal/${proposalId}/status/sent`,
  markProposalAsDraft: (proposalId: number) => `/proposal/${proposalId}/status/draft`,
  archiveProposal: (proposalId: number) => `/documents/${proposalId}/archive_schedule`,
  getPlanFeatures: (planId: number) => `/plan/${planId}/features`,
  refreshToken: '/authtoken',
  createPerson: '/person',
  documents: '/documents',
  documentDetail: function (documentId: string) {
    return `${this.documents}/${documentId}`;
  },
  previewDetails: (documentId: string) => `/preview/${documentId}`,
  sectionsBlocksForPreview: (documentId: string) => `/preview/${documentId}/grid`,
  documentImageLibraryImages: (contentId: string) => `/content/${contentId}/image-library`,
  documentUploadImage: (contentId: string) => `/content/${contentId}/image-library`,
  pdfImportIntoDocument: (contentId: string) => `/pdf-import/${contentId}`,
  accountSignees: (accountId: number) => `/accounts/${accountId}/signees`,
  getProposalContent: (proposalId: string) => `/documents/${proposalId}/content`,
  getDocumentPreviewPage: (documentId: string) => `/preview/${documentId}`,
  getSignaturesByDocumentId: (documentId: string) => `/documents/${documentId}/signatures`,
  ssoConfig: '/sso/oidc/config',
  updateSsoConfig: (providerId: string) => `/sso/oidc/config/${providerId}`,
  getSsoSchema: '/schemas/sso/openid_connect/config.json',
  getSsoRefSchema: (reference: string) => `/schemas/sso/openid_connect/${reference}`,
  discoverSso: '/sso/oidc/config/discover',
  ssoSettings: '/sso/settings',
  getRole: '/role',
  getAccountRoles: (accountId: number) => `/roles/account/${accountId}`,
  createRole: '/roles',
  updateRole: (roleId: string) => `/roles/${roleId}`,
  deleteRole: (roleId: string) => `/roles/${roleId}`,
  signSignature: () => '/signatures',
  getInstalledFonts: (contentId: string) => `/documents/${contentId}/fonts`,
  featureFlag: {
    optionalFeatures: '/optional_features',
    specificOptionalFeature: (flag: string) => `/optional_features/${flag}`,
  },
  getSalesforceObjectTypes: '/crm/salesforce/object_types',
  getSalesforceObjectTypesPrefix: (prefix: string) => `/crm/salesforce/object_types${prefix.length > 0 ? `/${prefix}` : ''}`,
  salesforceObjectSettings: '/crm/salesforce/settings',
  createDocument: '/proposal',
  getTemplateFolders: '/template/folder',
  uploadImage: '/upload/image',
  getLinkedAccounts: (accountId: number) => `/accounts/${accountId}/linked-accounts`,
  getWorkspaceMyAccounts: '/workspaces/my-accounts',
  postWorkspaceAccessUsage: '/workspaces/access-usages',
  postWorkspaceAccessAllowances: '/workspaces/access-allowances',
  getAccessAllowanceBySubdomain: (subdomain: string) => `/workspaces/access-allowances?account_subdomain=${subdomain}`,
  deleteAccessAllowanceBySubdomain: (uuid: string) => `/workspaces/access-allowances/${uuid}`,
  deleteDocument: (documentId: string) => `/documents/${documentId}`,
  documentsTrash: (documentId: string) => `/documents/${documentId}/trash`,
  getAPIClients: '/connect-api/apiclients',
  updateAPIClient: (id: string) => `/connect-api/apiclients/${id}`,
  rotateAPIClientSecret: (id: string) => `/connect-api/apiclients/${id}/rotate`,
  createAPIClient: '/connect-api/apiclients',
  getLocationData: '/listings/location_data',
  getPrismaticJWT: '/prismatic/embed-token',
  cancelAccountPlan: (accountId: number) => `/accounts/${accountId}/plan/cancel`,
  getPrismaticCredentials: '/prismatic/api-credentials',
  disconnectDataSourceByInstance: (instanceId: string) => `/flex-data/data-source/by-instance/${instanceId}`,
  getPaymentAccount: (accountId: number) => `/accounts/${accountId}/payment-account`,
  updatePaymentAccount: (accountId: number) => `/accounts/${accountId}/payment-account`,
  updateWorkspace: '/register/workspace',
  registerAccount: '/register/signupAccount',
  acceptInvitation: '/register/accept',
  checkSubdomainExists: (subdomain: string) => `/register/check-subdomain/${subdomain}`,
  getSubdomain: (accountId: number) => `/register/subdomain/${accountId}`,
  getInvitation: (token: string) => `/user/invite?token?=${token}`,
  getSeats: (accountId: number) => `/accounts/${accountId}/seats`,
  seatInvite: (accountId: number) => `/accounts/${accountId}/seats/invite`,
  addSeats: (accountId: number) => `/accounts/${accountId}/seats/update`,
  previewAddSeats: (accountId: number) => `/accounts/${accountId}/seats/preview`,
  unassignSeat: (accountId: number) => `/accounts/${accountId}/seats/unassign`,
  removeSeat: (accountId: number) => `/accounts/${accountId}/seats/remove`,
  resendInvite: (accountId: number) => `/accounts/${accountId}/seats/resend-invite`,
  getDocumentCollaborators: (documentId: string) => `/proposal/${documentId}/collaborators`,
  documentInvite: (documentId: string) => `/proposal/${documentId}/collaborators`,
  documentRemoveAccess: (documentId: string) => `/proposal/${documentId}/collaborators`,
  getFlexDataTopLevelObjectTypes: '/flex-data/top-level',
  getFlexDataLinks: (contentId: string) => `/document/${contentId}/links`,
  flexDataLink: (contentId: string) => `/document/${contentId}/link`,
  searchFlexDataLinks: '/flex-data/search',
  searchCrmCompanies: '/crm/companies/search',
  getCrmCompanyById: (id: CompositeEntityId) => `/crm/companies/${encodeURIComponent(id)}`,
  getCrmContactsByCompanyId: (id: CompositeEntityId) => `/crm/companies/${encodeURIComponent(id)}/contacts`,
  searchCrmContacts: '/crm/contacts/search',
  getCrmContactById: (id: CompositeEntityId) => `/crm/contacts/${encodeURIComponent(id)}`,
  getCrmCompaniesByContactId: (id: CompositeEntityId) => `/crm/contacts/${encodeURIComponent(id)}/companies`,
  legacyGetFlexDataLinks: (documentId: string) => `/flex-data/document/${documentId}/link`,
  legacyUpsertFlexDataLink: (documentId: string) => `/flex-data/document/${documentId}/link`,
  legacyUnlinkFlexDataLink: (documentId: string) => `/flex-data/document/${documentId}/unlink`,
  upsertFlexDataLink: (documentId: string) => `/flex-data/document/${documentId}/link`,
  unlinkFlexDataLink: (documentId: string) => `/flex-data/document/${documentId}/unlink`,
  getContentFlexDataLinks: (contentId: string) => `/content/${contentId}/DOCUMENT/links`,
  upsertContentFlexDataLink: (contentId: string) => `/content/${contentId}/DOCUMENT/link`,
  unlinkContentFlexDataLink: (contentId: string) => `/content/${contentId}/DOCUMENT/unlink`,
  v3IntegrationsAutomations: () => 'integrations',
  login: '/login/signin',
  isLoggedIn: '/login/check',
  getContacts: '/contacts',
  createContact: '/contacts',
  createCompany: '/companies',
  customFields: '/custom-fields',
  customFieldById: (id: string) => `/custom-fields/${id}`,
  signProsperStackPayload: (accountId: number) => `/accounts/${accountId}/prosperstack/sign`,
  recordPricingCatalogItemInsert: (itemId: string) => `/pricing-catalog/items/${itemId}/insert`,
};
